import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Pencil, KeyRound, UserCheck, UserX, Mail } from "lucide-react";
import { confirmDialog } from "@/lib/dialogs";

export default function UsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [caps, setCaps] = useState([]);
  const [presets, setPresets] = useState({});
  const [roleList, setRoleList] = useState([]);
  const labelOf = (k) => (roleList.find((r) => r.key === k)?.label) || ROLE_LABELS[k] || k;
  const [roleMgr, setRoleMgr] = useState(null);
  const [nu, setNu] = useState({ email: "", name: "", role: "proposal_user", can_amend_fee: true, permissions: [] });
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");

  const reloadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    reloadUsers();
    api.get("/capabilities").then((r) => { setCaps(r.data.capabilities || []); setPresets(r.data.presets || {}); setRoleList(r.data.roles || []); setNu((n) => ({ ...n, permissions: r.data.presets?.[n.role] || [] })); }).catch(() => {});
  }, []);
  const reloadCaps = () => api.get("/capabilities").then((r) => { setPresets(r.data.presets || {}); setRoleList(r.data.roles || []); });

  const togglePerm = (setter, obj, key) => setter({ ...obj, permissions: (obj.permissions || []).includes(key) ? obj.permissions.filter((x) => x !== key) : [...(obj.permissions || []), key] });

  const saveRole = async (i) => {
    const r = roleMgr.rows[i];
    if (!(r.label || "").trim()) { toast.error("Give the role a name"); return; }
    try {
      if (r.key) await api.put(`/roles/${r.key}`, { label: r.label, permissions: r.permissions || [] });
      else await api.post("/roles", { label: r.label, permissions: r.permissions || [] });
      toast.success("Role saved");
      const { data } = await api.get("/capabilities");
      setPresets(data.presets || {}); setRoleList(data.roles || []);
      setRoleMgr({ open: true, rows: (data.roles || []).filter((x) => x.custom).map((x) => ({ ...x })) });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save role"); }
  };
  const deleteRole = async (i) => {
    const r = roleMgr.rows[i];
    if (!r.key) { setRoleMgr((m) => ({ ...m, rows: m.rows.filter((_, j) => j !== i) })); return; }
    const ok = await confirmDialog({ title: "Delete role?", description: `Remove the "${r.label}" role?`, confirmText: "Delete", tone: "destructive" });
    if (!ok) return;
    try { await api.delete(`/roles/${r.key}`); toast.success("Role deleted"); await reloadCaps(); setRoleMgr((m) => ({ ...m, rows: m.rows.filter((_, j) => j !== i) })); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const createUser = async () => {
    if (!nu.email || !nu.name) return toast.error("Name and email required");
    try {
      const { data } = await api.post("/users", nu);
      toast.success(data.invite_sent ? "User created — invite email sent" : "User created (invite email couldn't be sent — check email settings)");
      setNu({ email: "", name: "", role: "proposal_user", can_amend_fee: true, permissions: presets.proposal_user || [] });
      reloadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/invite`); toast.success("Invite re-sent"); }
    catch { toast.error("Could not resend invite"); }
  };
  const saveUser = async () => {
    try {
      await api.put(`/users/${editUser.id}`, { name: editUser.name, role: editUser.role, can_amend_fee: editUser.can_amend_fee, permissions: editUser.permissions });
      toast.success("User updated"); setEditUser(null); reloadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: u.active === false });
      toast.success(u.active === false ? "User activated" : "User deactivated"); reloadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doResetPw = async () => {
    if ((pwValue || "").length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { password: pwValue });
      toast.success("Password reset"); setPwUser(null); setPwValue("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteUser = async (u) => {
    if (!(await confirmDialog({ title: "Delete user?", description: `Permanently delete ${u.name} (${u.email})? This cannot be undone.`, confirmText: "Delete", tone: "destructive" }))) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-4 space-y-4">
      <Card className="p-6 rounded-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-alt font-semibold text-cass-navy">Create user</h3>
          <Button size="sm" variant="outline" onClick={() => setRoleMgr({ open: true, rows: roleList.filter((r) => r.custom).map((r) => ({ ...r })) })} data-testid="manage-roles-btn"><UserCheck className="w-4 h-4 mr-1" />Manage roles</Button>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <Input placeholder="Name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="nu-name" />
          <Input placeholder="Email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="nu-email" />
          <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v, permissions: presets[v] || [] })}>
            <SelectTrigger data-testid="nu-role"><SelectValue /></SelectTrigger>
            <SelectContent>{roleList.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="text-xs text-cass-slate mt-2">The user receives an email invite to set their own password. Role sets sensible defaults below — tick to customise what they can see / do.</p>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 mt-3 border border-border rounded-md p-3" data-testid="nu-perms">
          {caps.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-xs text-cass-navy">
              <input type="checkbox" disabled={nu.role === "super_admin"} checked={nu.role === "super_admin" || (nu.permissions || []).includes(c.key)} onChange={() => togglePerm(setNu, nu, c.key)} data-testid={`nu-perm-${c.key}`} />
              {c.label}
            </label>
          ))}
          {nu.role === "super_admin" && <p className="text-[11px] text-cass-slate col-span-2">Super Administrators always have full access.</p>}
        </div>
        <label className="flex items-center gap-2 text-sm mt-3 text-cass-slate">
          <input type="checkbox" checked={nu.can_amend_fee} onChange={(e) => setNu({ ...nu, can_amend_fee: e.target.checked })} /> Can amend calculated fees
        </label>
        <Button className="mt-3 bg-cass-teal text-white" onClick={createUser} data-testid="create-user-btn"><Mail className="w-4 h-4 mr-1" />Create &amp; send invite</Button>
      </Card>
      <Card className="p-6 rounded-md">
        {users.map((u) => {
          const isSelf = u.id === currentUserId;
          const inactive = u.active === false;
          return (
            <div key={u.id} className={`flex items-center justify-between border-b border-border py-2 text-sm ${inactive ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
              <div><span className="font-medium text-cass-navy">{u.name}</span> <span className="text-cass-slate">· {u.email}</span></div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{labelOf(u.role)}</Badge>
                {u.can_amend_fee && <Badge variant="outline">amend fees</Badge>}
                {inactive && <Badge variant="destructive" data-testid={`user-inactive-${u.id}`}>Inactive</Badge>}
                {!u.has_password && <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid={`user-pending-${u.id}`}>Pending invite</Badge>}
                {!u.has_password && <Button size="sm" variant="ghost" title="Resend invite" onClick={() => resendInvite(u)} data-testid={`user-invite-${u.id}`}><Mail className="w-4 h-4" /></Button>}
                <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditUser({ id: u.id, name: u.name, role: u.role, can_amend_fee: u.can_amend_fee !== false, permissions: u.permissions || presets[u.role] || [] })} data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Reset password" onClick={() => { setPwUser(u); setPwValue(""); }} data-testid={`user-resetpw-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
                {!isSelf && <Button size="sm" variant="ghost" title={inactive ? "Activate" : "Deactivate"} onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>{inactive ? <UserCheck className="w-4 h-4 text-emerald-600" /> : <UserX className="w-4 h-4 text-red-600" />}</Button>}
                {!isSelf && <Button size="sm" variant="ghost" title="Delete" onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
              </div>
            </div>
          );
        })}
      </Card>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div><Label>Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleList.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-cass-slate"><input type="checkbox" checked={editUser.can_amend_fee} onChange={(e) => setEditUser({ ...editUser, can_amend_fee: e.target.checked })} data-testid="edit-user-amend" /> Can amend calculated fees</label>
              <div>
                <Label>Access rights</Label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 border border-border rounded-md p-3 max-h-56 overflow-y-auto" data-testid="edit-user-perms">
                  {caps.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 text-xs text-cass-navy">
                      <input type="checkbox" disabled={editUser.role === "super_admin"} checked={editUser.role === "super_admin" || (editUser.permissions || []).includes(c.key)} onChange={() => togglePerm(setEditUser, editUser, c.key)} data-testid={`edit-perm-${c.key}`} />
                      {c.label}
                    </label>
                  ))}
                  {editUser.role === "super_admin" && <p className="text-[11px] text-cass-slate col-span-2">Super Administrators always have full access.</p>}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button><Button className="bg-cass-teal text-white" onClick={saveUser} data-testid="edit-user-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password</DialogTitle><DialogDescription>{pwUser?.email}</DialogDescription></DialogHeader>
          <div><Label>New password</Label><Input type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} data-testid="reset-pw-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button><Button className="bg-cass-teal text-white" onClick={doResetPw} data-testid="reset-pw-save">Reset password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!roleMgr} onOpenChange={(o) => !o && setRoleMgr(null)}>
        <DialogContent className="max-w-2xl" data-testid="manage-roles-dialog">
          <DialogHeader><DialogTitle>Custom roles</DialogTitle>
            <DialogDescription>Create your own roles as a named bundle of access rights. Built-in roles can't be edited here.</DialogDescription></DialogHeader>
          {roleMgr && (
            <div className="space-y-3 max-h-[460px] overflow-y-auto">
              {roleMgr.rows.map((r, i) => (
                <div key={i} className="border border-border rounded-md p-3 space-y-2" data-testid={`role-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input value={r.label} onChange={(e) => setRoleMgr((m) => ({ ...m, rows: m.rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) }))} placeholder="Role name" data-testid={`role-label-${i}`} />
                    <Button size="sm" className="bg-cass-teal text-white" onClick={() => saveRole(i)} data-testid={`role-save-${i}`}>Save</Button>
                    <button onClick={() => deleteRole(i)} className="text-red-500 hover:text-red-700" title="Delete role" data-testid={`role-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 border border-border rounded-md p-2">
                    {caps.map((c) => (
                      <label key={c.key} className="flex items-center gap-2 text-xs text-cass-navy">
                        <input type="checkbox" checked={(r.permissions || []).includes(c.key)} onChange={() => setRoleMgr((m) => ({ ...m, rows: m.rows.map((x, j) => (j === i ? { ...x, permissions: (x.permissions || []).includes(c.key) ? x.permissions.filter((p) => p !== c.key) : [...(x.permissions || []), c.key] } : x)) }))} data-testid={`role-perm-${i}-${c.key}`} />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setRoleMgr((m) => ({ ...m, rows: [...m.rows, { label: "", permissions: [] }] }))} data-testid="role-add"><UserCheck className="w-4 h-4 mr-1" />Add role</Button>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setRoleMgr(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
